/**
 * InviteCodeActivationOverlay.jsx
 *
 * INTRUSIVE, BLOCKING overlay shown to creator/model users when the admin
 * has activated their invite/promo code.
 *
 * Rules (stricter than BroadcastAlertOverlay):
 * - Only visible to role = "creator" / "model"
 * - Polls GET /api/models/me/invite-activation every 30 s + on every route change
 * - NO close button, NO backdrop click, NO Escape key — NOTHING closes it
 * - The ONLY way to dismiss it is clicking "He leído y acepto"
 * - On accept → POST /api/models/me/invite-activation/{id}/acknowledge
 * - z-index 9999 (above BroadcastAlertOverlay at 9998)
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { Tag, Check, Loader2, ShieldCheck } from "lucide-react";
import { API } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

const POLL_MS = 30_000;
const SUPPRESSED_ROUTES = ["/login", "/register"];

function fmt(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("es-ES", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return ""; }
}

export default function InviteCodeActivationOverlay() {
  const { user } = useAuth();
  const location = useLocation();
  const [activation, setActivation] = useState(null);
  const [accepting, setAccepting] = useState(false);
  const timerRef = useRef(null);

  const fetchPending = useCallback(async () => {
    if (!user) return;
    if (!["creator", "model"].includes(user.role)) return;
    try {
      const { data } = await axios.get(`${API}/models/me/invite-activation`, { withCredentials: true });
      setActivation(data.pending ? data : null);
    } catch {
      /* silent — never break the app */
    }
  }, [user]);

  // Re-check on every route change + poll
  useEffect(() => {
    fetchPending();
    timerRef.current = setInterval(fetchPending, POLL_MS);
    return () => clearInterval(timerRef.current);
  }, [fetchPending, location.pathname]);

  // Block keyboard Escape completely when overlay is visible
  useEffect(() => {
    if (!activation) return;
    const block = (e) => {
      if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); }
    };
    window.addEventListener("keydown", block, true);
    return () => window.removeEventListener("keydown", block, true);
  }, [activation]);

  const isSuppressed = SUPPRESSED_ROUTES.some(r => location.pathname.startsWith(r));
  if (!activation || isSuppressed || !user || !["creator", "model"].includes(user.role)) return null;

  const handleAccept = async () => {
    setAccepting(true);
    try {
      await axios.post(
        `${API}/models/me/invite-activation/${activation.id}/acknowledge`,
        {},
        { withCredentials: true }
      );
      setActivation(null);
    } catch {
      /* retry on next poll */
    } finally {
      setAccepting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.92)" }}
      data-testid="invite-activation-overlay"
      // Intentionally no onClick on backdrop — cannot be dismissed that way
    >
      {/* Blur layer */}
      <div className="absolute inset-0 backdrop-blur-md" />

      {/* Card */}
      <div
        className="relative w-full max-w-sm rounded-2xl overflow-hidden shadow-2xl"
        style={{ border: "1px solid rgba(212,175,55,0.5)", background: "#0f0f0f" }}
        data-testid="invite-activation-card"
        onClick={e => e.stopPropagation()}
      >
        {/* Animated top bar */}
        <div
          className="h-1.5 w-full"
          style={{ background: "linear-gradient(90deg, #d4af37, #f5c842, #d4af37)", backgroundSize: "200% 100%", animation: "shimmer 2s linear infinite" }}
        />

        {/* Glow ring behind icon */}
        <div className="flex justify-center pt-6 pb-2">
          <div
            className="w-16 h-16 rounded-2xl flex items-center justify-center"
            style={{ background: "rgba(212,175,55,0.12)", border: "1.5px solid rgba(212,175,55,0.35)", boxShadow: "0 0 24px rgba(212,175,55,0.2)" }}
          >
            <Tag className="w-7 h-7" style={{ color: "#d4af37" }} />
          </div>
        </div>

        {/* Header text */}
        <div className="text-center px-5 pb-4">
          <p className="text-[10px] uppercase tracking-[0.2em] font-heading font-bold mb-1" style={{ color: "rgba(212,175,55,0.7)" }}>
            STARTIP · Aviso importante
          </p>
          <h2 className="text-base font-heading font-black text-white uppercase tracking-wide leading-tight">
            ¡Código Activado!
          </h2>
        </div>

        {/* Divider */}
        <div className="mx-5 h-px" style={{ background: "rgba(255,255,255,0.07)" }} />

        {/* Code badge */}
        <div className="mx-5 mt-4 flex items-center justify-center">
          <div
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl font-mono font-bold text-sm"
            style={{ background: "rgba(212,175,55,0.1)", border: "1px solid rgba(212,175,55,0.3)", color: "#f5c842" }}
            data-testid="activation-code-badge"
          >
            <ShieldCheck className="w-4 h-4 flex-shrink-0" />
            {activation.code}
          </div>
        </div>

        {/* Message */}
        <div className="px-5 py-4">
          <p className="text-sm text-zinc-300 font-body leading-relaxed text-center" data-testid="activation-message">
            {activation.message}
          </p>
        </div>

        {/* Meta */}
        <div className="px-5 pb-4 text-center">
          <p className="text-[10px] text-zinc-600 font-body">
            Activado el {fmt(activation.activated_at)} por {activation.admin_name}
          </p>
        </div>

        {/* Divider */}
        <div className="mx-5 h-px" style={{ background: "rgba(255,255,255,0.07)" }} />

        {/* Accept button — THE ONLY WAY TO CLOSE */}
        <div className="p-5">
          <button
            onClick={handleAccept}
            disabled={accepting}
            data-testid="invite-activation-accept-btn"
            className="w-full h-12 rounded-xl font-heading font-black uppercase text-sm tracking-wide transition-all disabled:opacity-50 flex items-center justify-center gap-2"
            style={{
              background: accepting ? "rgba(212,175,55,0.5)" : "#d4af37",
              color: "#0a0a0a",
              boxShadow: "0 4px 16px rgba(212,175,55,0.3)",
            }}
          >
            {accepting
              ? <><Loader2 className="w-4 h-4 animate-spin" />Confirmando…</>
              : <><Check className="w-4 h-4" />He leído y acepto</>
            }
          </button>
          <p className="text-center text-[10px] text-zinc-600 mt-2 font-body">
            Este aviso no desaparecerá hasta que lo confirmes
          </p>
        </div>
      </div>

      <style>{`
        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
      `}</style>
    </div>
  );
}
